<template>
  <div class="tw-h-full">
    <div
      class="tw-h-[50px] tw-w-full tw-relative tw-z-10 tw-bg-white tw-border-b tw-border-solid tw-border-gray-200 tw-flex tw-items-center tw-justify-between tw-px-4">
      <h2 class="tw-font-semibold tw-text-lg">{{ $t('pages.dashboard.overview') }}</h2>
      <div>
        <a-button @click="show.filters = !show.filters" intent="simple" class="tw-py-0 !tw-min-h-[30px] "
          left-icon="lucide:list-filter">
          <span>{{ $t('buttons.filter') }}</span>
        </a-button>
      </div>
    </div>

    <div :class="[show.filters ? 'tw-grid-rows-[1fr]' : 'tw-grid-rows-[0fr]']"
      class="tw-grid tw-duration-300 tw-transition-all">
      <div class="tw-overflow-hidden tw-col-span-1">
        <div class="tw-h-[200px] tw-bg-white tw-w-full -tw-translate-y-1 tw-border-b tw-border-solid tw-border-gray-200 tw-p-4">
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
            <DateRangeFilter />
            <ProductFilter />
            <SourceFilter />
          </div>
          
          <div class="tw-mt-4 tw-flex tw-items-center tw-justify-end tw-gap-2">
            <a-button @click="onClearFilters" intent="simple" class="!tw-w-[80px] !tw-h-[30px] !tw-min-h-0 tw-text-xs">
              {{ $t('buttons.clear') }}
            </a-button>
            <a-button @click="onApplyFilters" class="!tw-w-[80px] !tw-h-[30px] !tw-min-h-0 tw-text-xs">
              {{ $t('buttons.filter') }}
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <div class="tw-p-2 tw-w-full">
      <ProgressBar />
    </div>

    <div class="tw-px-2">
      <div class="tw-grid md:tw-grid-cols-2 lg:tw-grid-cols-5 tw-grid-cols-1 tw-gap-2">
        <div v-for="w in widgets" :key="w"
          class="tw-w-full tw-p-4 tw-h-[100px]  tw-bg-white tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-flex tw-flex-col tw-justify-between">
          
          <!-- Loading State -->
          <div v-if="loading" class="tw-flex tw-flex-col tw-justify-between tw-h-full">
            <div class="tw-h-4 tw-w-3/4 tw-bg-gray-200 tw-animate-pulse tw-rounded"></div>
            <div class="tw-flex tw-items-center tw-justify-center tw-flex-1">
              <icon icon="eos-icons:three-dots-loading" class="tw-text-3xl tw-text-gray-400" />
            </div>
          </div>

          <!-- Loaded State -->
          <div v-else class="tw-flex tw-flex-col tw-justify-between tw-h-full">
            <div>
              <p class="tw-text-sm tw-font-medium tw-text-gray-500">{{ $t(w.title) }}</p>
            </div>
            <h2 class="tw-text-3xl tw-text-black tw-font-bold tw-mb-1">{{ w.value }}</h2>

            <div v-if="false" class="tw-flex tw-items-center tw-gap-2 ">
              <div class="tw-flex tw-items-center tw-px-1 tw-rounded tw-font-medium"
                :class="[w.color === 'green' ? 'tw-text-emerald-500 tw-bg-emerald-50' : 'tw-bg-red-50 tw-text-red-500']">
                <icon :icon="w.icon" class="tw-text-base" />
                <span class="tw-text-sm">{{ w.percentage }}</span>
              </div>
              <p class="tw-text-gray-600 tw-text-sm">{{ $t(w.comparedTo) }}</p>
            </div>
          </div>
        </div>
        
        <!-- User Ranking Widget -->
        <UserRankingWidget />

      </div>
    </div>

    <div class="tw-px-2 tw-mt-2">
      <div class="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 tw-grid-cols-1 tw-gap-2">
        <LeadsPerDayChart />
        <SpendPerDayChart />
        <CostPerLeadChart />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, provide } from 'vue';
import ProgressBar from '@/components/dashboard/marketer/overview/ProgressBar.vue';
import DateRangeFilter from '@/components/dashboard/marketer/filters/DateRangeFilter.vue';
import ProductFilter from '@/components/dashboard/marketer/filters/ProductFilter.vue';
import SourceFilter from '@/components/dashboard/marketer/filters/SourceFilter.vue';
import LeadsPerDayChart from '@/components/dashboard/marketer/charts/LeadsPerDayChart.vue';
import SpendPerDayChart from '@/components/dashboard/marketer/charts/SpendPerDayChart.vue';
import CostPerLeadChart from '@/components/dashboard/marketer/charts/CostPerLeadChart.vue';
import UserRankingWidget from '@/components/dashboard/marketer/widgets/UserRankingWidget.vue';
import { marketer } from '@/api/dashboard';

const show = ref({ filters: false });
const loading = ref(false);

// Filter state management
const filters = ref({
  date_range: {
    from: null,
    to: null
  },
  product_id: [],
  source_id: []
});

// Provide filter data to child components

const onApplyFilters = () => {
  console.log('Applied filters:', filters.value);
  // TODO: Implement filter logic to refresh dashboard data
  // This is where you would call your API with the filter parameters
  show.value.filters = false;
};
provide('filters', filters);
provide('filter', onApplyFilters);

const onClearFilters = () => {
  filters.value = {
    date_range: {
      from: null,
      to: null
    },
    product_id: [],
    source_id: []
  };
  console.log('Cleared filters');
  // TODO: Refresh dashboard data without filters
};

const widgets = ref([
  {
    title: 'common.total-leads',
    value: '3,450',
    percentage: '20.00%',
    comparedTo: 'common.compared-to-last-month',
    icon: 'jam:arrow-up',
    color: 'green',
  },
  {
    title: 'common.total-spend',
    value: '$345.00',
    percentage: '12.00%',
    comparedTo: 'common.compared-to-last-month',
    icon: 'jam:arrow-up',
    color: 'red',
  },
  {
    title: 'common.cost-per-lead',
    value: '$2.00',
    percentage: '12.10%',
    comparedTo: 'common.compared-to-last-month',
    icon: 'jam:arrow-down',
    color: 'green',
  },
  {
    title: 'common.total-commission',
    value: '$12,456.00',
    percentage: '124.20%',
    comparedTo: 'common.compared-to-last-month',
    icon: 'jam:arrow-up',
    color: 'green',
  },

])


const getData = async () => {
  loading.value = true;
  await marketer.overview(filters.value)
  .then(res => {
    console.log(res);
    widgets.value[0].value = res.data.total_leads;
    widgets.value[1].value =  formatNumber(res.data.total_spend);
    widgets.value[2].value = formatNumber(res.data.cost_per_lead);
    widgets.value[3].value = formatNumber(res.data.total_commission);
  })
  .catch(err => {
    console.log(err);
  })
  
  loading.value = false;
}

const formatNumber = (number, type = {style: 'currency', currency: 'PKR'}) => {
    return new Intl.NumberFormat('en', { maximumFractionDigits: 2, ...type }).format(number);
}

getData();
</script>

<style></style>